import { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useMatch } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Plus } from '@phosphor-icons/react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RealtimeProvider } from './context/RealtimeContext';
import { I18nProvider, useT } from './i18n/I18nContext';
import AuthPage from './pages/AuthPage';
import ProjectSwitcher from './components/ProjectSwitcher';
import UserMenu from './components/UserMenu';
import NewProjectDialog from './components/NewProjectDialog';
import ProjectBoard from './pages/ProjectBoard';
import StatsDashboard from './pages/StatsDashboard';
import api from './lib/api';

// "/" has no page of its own: open the first project, or offer to create one.
const Home = ({ projects, loaded, onNewProject }) => {
  const t = useT();
  if (!loaded) return <div className="flex-1 flex items-center justify-center text-zinc-500">{t('common.loading')}</div>;
  if (projects.length > 0) return <Navigate to={`/projects/${projects[0].id}`} replace />;
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 bg-zinc-50 p-6" data-testid="no-projects">
      <p className="text-sm text-zinc-500">{t('sidebar.noProjects')}</p>
      <button
        type="button"
        data-testid="empty-new-project-btn"
        onClick={onNewProject}
        className="bg-black text-white px-5 py-2.5 rounded-sm text-sm font-semibold hover:bg-zinc-800 inline-flex items-center gap-2"
      >
        <Plus size={14} /> {t('sidebar.newProject')}
      </button>
    </div>
  );
};

const ProtectedShell = () => {
  const { user, loading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const location = useLocation();
  const onBoard = useMatch('/projects/:projectId');
  const token = typeof window !== 'undefined' ? localStorage.getItem('kt_token') : null;

  const loadProjects = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/projects');
      setProjects(data);
    } catch (e) {
    } finally {
      setProjectsLoaded(true);
    }
  }, [user]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  if (loading) return <div className="flex h-screen items-center justify-center text-zinc-500">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  return (
    <RealtimeProvider token={token}>
      <div className="flex flex-col h-screen w-full overflow-hidden bg-white">
        {/* The board renders the switcher + user menu in its own header. */}
        {!onBoard && (
          <header className="border-b border-zinc-200 px-4 md:px-8 py-3 bg-white flex items-center gap-3 flex-shrink-0">
            <ProjectSwitcher projects={projects} onNewProject={() => setNewProjectOpen(true)} />
            <div className="ml-auto">
              <UserMenu />
            </div>
          </header>
        )}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <Routes>
            <Route
              path="/"
              element={<Home projects={projects} loaded={projectsLoaded} onNewProject={() => setNewProjectOpen(true)} />}
            />
            <Route path="/stats" element={<StatsDashboard />} />
            <Route
              path="/projects/:projectId"
              element={<ProjectBoard projects={projects} onProjectsChange={loadProjects} onNewProject={() => setNewProjectOpen(true)} />}
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
        <NewProjectDialog open={newProjectOpen} onOpenChange={setNewProjectOpen} onProjectsChange={loadProjects} />
      </div>
    </RealtimeProvider>
  );
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-zinc-500">Loading...</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function App() {
  useEffect(() => {
    document.title = 'CMR — Create Merge Request';
  }, []);

  return (
    <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<PublicOnly><AuthPage /></PublicOnly>} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/*" element={<ProtectedShell />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}

export default App;
